import React, { useRef, useEffect, useCallback } from "react";
import {
  Plus,
  Trash2,
  Save,
  Printer,
  X,
  XCircle,
  Send,
  Loader2,
  Check,
  CheckCircle2,
  ArrowLeft,
  UserPlus,
  Eye,
} from "lucide-react";
import { printVisibleStandardForm } from "../../utils/printStandardForm";
export {
  ExpenseTable,
  getDefaultExpenseTableValue,
  getLpoFinancialTotalsFromItems,
} from "./ExpenseTable";

const DEFAULT_CONTROL_CLASS =
  "mt-1 w-full  px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-primary-dark focus:outline-none focus:ring-2 focus:ring-primary-dark/30";
// Uses accent-color so checked state follows app theme.
const DEFAULT_CHECKBOX_CLASS =
  "h-4 w-4 rounded border border-cream/60 bg-surface text-primary accent-primary focus:ring-2 focus:ring-primary/30 focus:ring-offset-2 focus:ring-offset-surface disabled:cursor-not-allowed disabled:opacity-60";

/**
 * @param {'left'|'top'} [labelPosition='top'] - 'top' = label above input; 'left' = label in left column
 */
export const FormField = ({
  id,
  label,
  required = false,
  error,
  children,
  labelWidthRem = 8,
  labelPosition = "top",
  className = "",
  labelClassName = "",
}) => {
  const errorId = id ? `${id}-error` : undefined;
  const isTop = labelPosition === "top";

  const labelEl = label ? (
    <label
      htmlFor={id}
      className={labelClassName || "text-sm font-medium text-primary-dark"}
    >
      {label} {required && <span data-required-indicator="true" className="text-red-500">*</span>}
    </label>
  ) : null;

  const fieldBlock = (
    <div className="min-w-0">
      {children}
      {error && (
        <p id={errorId} className="mt-1 text-xs text-red-600">
          {error}
        </p>
      )}
    </div>
  );

  if (isTop) {
    return (
      <div className={["space-y-1", className].filter(Boolean).join(" ")}>
        {labelEl}
        {fieldBlock}
      </div>
    );
  }

  return (
    <div
      className={["grid items-center", className].filter(Boolean).join(" ")}
      style={{ gridTemplateColumns: `${labelWidthRem}rem 1fr` }}
    >
      {labelEl ?? <div />}
      {fieldBlock}
    </div>
  );
};

const READ_ONLY_CONTROL_CLASS =
  "w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-slate-700 shadow-sm outline-none";
const READ_ONLY_MULTILINE_CLASS =
  "w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-slate-700 shadow-sm whitespace-pre-wrap break-words";

export const ReadOnlyFieldValue = ({
  id,
  label,
  required = false,
  error,
  value = "",
  labelWidthRem,
  labelPosition = "top",
  labelClassName,
  wrapperClassName = "",
  controlClassName = "",
  multiline = false,
  rows = 3,
  placeholder = "—",
}) => {
  const normalizedValue =
    value === undefined || value === null || value === "" ? placeholder : String(value);
  const normalizedRows = Number.isFinite(Number(rows)) ? Math.max(1, Number(rows)) : 3;

  return (
    <FormField
      id={id}
      label={label}
      required={required}
      error={error}
      labelWidthRem={labelWidthRem}
      labelPosition={labelPosition}
      labelClassName={labelClassName}
      className={wrapperClassName}
    >
      {multiline ? (
        <div
          id={id}
          data-read-only-multiline="true"
          role="textbox"
          aria-readonly="true"
          aria-multiline="true"
          className={[READ_ONLY_MULTILINE_CLASS, controlClassName]
            .filter(Boolean)
            .join(" ")}
          style={{ minHeight: `${normalizedRows * 1.5 + 1}rem` }}
        >
          {normalizedValue}
        </div>
      ) : (
        <input
          id={id}
          type="text"
          readOnly
          value={normalizedValue}
          className={[READ_ONLY_CONTROL_CLASS, controlClassName].filter(Boolean).join(" ")}
        />
      )}
    </FormField>
  );
};

const INPUT_BASE_CLASSES =
  "w-full rounded-lg px-2 py-1.5 text-xs text-slate-700 border border-gray-300 bg-white shadow-sm focus:border-primary-dark focus:outline-none focus:ring-1 focus:ring-primary-dark/40 transition";

export const TextInput = ({
  id,
  label,
  required = false,
  error,
  labelWidthRem,
  labelPosition,
  labelClassName,
  controlClassName,
  ...props
}) => (
  <FormField
    id={id}
    label={label}
    required={required}
    error={error}
    labelWidthRem={labelWidthRem}
    labelPosition={labelPosition}
    labelClassName={labelClassName}
  >
    <input
      id={id}
      className={[INPUT_BASE_CLASSES, controlClassName]
        .filter(Boolean)
        .join(" ")}
      aria-invalid={error ? true : undefined}
      aria-describedby={error ? `${id}-error` : undefined}
      {...props}
    />
  </FormField>
);

export const DateTimeInput = ({
  id,
  label,
  required = false,
  error,
  labelWidthRem,
  labelPosition,
  controlClassName,
  ...props
}) => (
  <FormField
    id={id}
    label={label}
    required={required}
    error={error}
    labelWidthRem={labelWidthRem}
    labelPosition={labelPosition}
  >
    <input
      id={id}
      type="datetime-local"
      className={[INPUT_BASE_CLASSES, controlClassName]
        .filter(Boolean)
        .join(" ")}
      aria-invalid={error ? true : undefined}
      aria-describedby={error ? `${id}-error` : undefined}
      {...props}
    />
  </FormField>
);

const SELECT_BASE_CLASSES =
  "w-full rounded-lg px-2 py-1.5 text-xs text-slate-700 border border-gray-300 bg-white shadow-sm focus:border-primary-dark focus:outline-none focus:ring-1 focus:ring-primary-dark/40 transition";

export const SelectInput = ({
  id,
  label,
  required = false,
  error,
  wrapperClassName = "",
  labelClassName,
  labelWidthRem,
  labelPosition,
  controlClassName,
  children,
  ...props
}) => (
  <FormField
    id={id}
    label={label}
    required={required}
    error={error}
    labelWidthRem={labelWidthRem}
    labelPosition={labelPosition}
    className={wrapperClassName}
    labelClassName={labelClassName}
  >
    <select
      id={id}
      className={[SELECT_BASE_CLASSES, controlClassName]
        .filter(Boolean)
        .join(" ")}
      aria-invalid={error ? true : undefined}
      aria-describedby={error ? `${id}-error` : undefined}
      {...props}
    >
      {children}
    </select>
  </FormField>
);

export const Sectiontitle = ({ title, className = "" }) => (
  <h2
    className={`text-sm font-semibold text-primary-dark uppercase my-2 ${className}`.trim()}
  >
    {title}
  </h2>
);

const TEXTAREA_BASE_CLASS =
  "w-full rounded-lg px-2 py-1.5 text-sm text-slate-700 border border-gray-300 bg-white shadow-sm focus:border-primary-dark focus:outline-none focus:ring-1 focus:ring-primary-dark/40 transition";

/**
 * Textarea that grows in height as the user types. Good for memo body and long content.
 * Uses a minimum height (minRows) so it starts large enough; height increases with content.
 *
 * @param {Object} props - Same as TextArea; rows is used as minRows for initial/min height
 */
export const AutoResizeTextarea = ({
  id,
  label,
  required = false,
  error,
  wrapperClassName = "",
  labelClassName,
  controlClassName = DEFAULT_CONTROL_CLASS,
  labelWidthRem,
  labelPosition,
  rows: minRows = 6,
  value = "",
  onChange,
  ...props
}) => {
  const textareaRef = useRef(null);

  const adjustHeight = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    const lineHeight = 22;
    const minHeight = minRows * lineHeight;
    const newHeight = Math.max(minHeight, el.scrollHeight);
    el.style.height = `${newHeight}px`;
  }, [minRows]);

  useEffect(() => {
    adjustHeight();
    const raf = requestAnimationFrame(() => adjustHeight());
    return () => cancelAnimationFrame(raf);
  }, [value, adjustHeight]);

  return (
    <FormField
      id={id}
      label={label}
      required={required}
      error={error}
      labelWidthRem={labelWidthRem}
      labelPosition={labelPosition}
      className={wrapperClassName}
      labelClassName={labelClassName}
    >
      <textarea
        ref={textareaRef}
        id={id}
        className={`${TEXTAREA_BASE_CLASS} resize-y overflow-hidden min-h-0 ${controlClassName}`.trim()}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-error` : undefined}
        rows={minRows}
        value={value}
        onChange={onChange}
        {...props}
      />
    </FormField>
  );
};

export const TextArea = ({
  id,
  label,
  required = false,
  error,
  wrapperClassName = "",
  labelClassName,
  controlClassName = DEFAULT_CONTROL_CLASS,
  labelWidthRem,
  labelPosition,
  ...props
}) => (
  <FormField
    id={id}
    label={label}
    required={required}
    error={error}
    labelWidthRem={labelWidthRem}
    labelPosition={labelPosition}
    className={wrapperClassName}
    labelClassName={labelClassName}
  >
    <textarea
      id={id}
      className="
        w-full rounded-lg px-2 py-1.5 text-xs text-slate-700 border border-gray-300 bg-white shadow-sm focus:border-primary-dark focus:outline-none focus:ring-1 focus:ring-primary-dark/40 transition resize-y
      "
      aria-invalid={error ? true : undefined}
      aria-describedby={error ? `${id}-error` : undefined}
      {...props}
    />
  </FormField>
);

export const CheckboxField = ({
  id,
  label,
  checked,
  onChange,
  disabled,

  checkboxClassName = DEFAULT_CHECKBOX_CLASS,
  labelClassName = "text-sm font-medium text-slate-600",
}) => (
  <div className={`flex items-center gap-3`.trim()}>
    <input
      id={id}
      type="checkbox"
      checked={checked}
      onChange={onChange}
      className={[
        "h-2 w-2  bg-white text-primary accent-primary focus:ring-2 focus:ring-primary/30 focus:ring-offset-2 focus:ring-offset-surface disabled:cursor-not-allowed disabled:opacity-60",
        checkboxClassName,
      ]
        .filter(Boolean)
        .join(" ")}
      disabled={disabled}
    />
    {label ? (
      <label htmlFor={id} className={labelClassName}>
        {label}
      </label>
    ) : null}
  </div>
);

export const CheckboxInline = ({
  id,
  label,
  checked,
  onChange,
  disabled,
  wrapperClassName = "",
  checkboxClassName = DEFAULT_CHECKBOX_CLASS,
  textClassName = "text-sm text-slate-600",
  /** Top-align checkbox with multi-line labels (e.g. name + description). */
  alignStart = false,
}) => (
  <label
    htmlFor={id}
    className={`flex gap-2 ${alignStart ? "items-start" : "items-center"} ${wrapperClassName}`.trim()}
  >
    <input
      id={id}
      type="checkbox"
      checked={checked}
      onChange={onChange}
      className={`${checkboxClassName} ${alignStart ? "mt-0.5 shrink-0" : ""}`.trim()}
      disabled={disabled}
    />
    <span className={textClassName}>{label}</span>
  </label>
);

export const FormFieldset = ({ children }) => (
  <fieldset className="min-w-0">{children}</fieldset>
);

// ─────────────────────────────────────────────────────────────────────────────
// MinutesTable – table for meeting minutes
// ─────────────────────────────────────────────────────────────────────────────

const MINUTES_INPUT_CLASS =
  "w-full rounded-lg px-2 py-1.5 text-xs text-slate-700 border border-gray-300 bg-surface-muted shadow-sm focus:border-primary-dark focus:outline-none focus:ring-1 focus:ring-primary-dark/40 transition";

/** Default structure for minutes table value. Date stored as ISO string for datetime (e.g. Jul 28, 2025, 5:19 pm). */
export const getDefaultMinutesTableValue = (defaultRows = []) => ({
  rows: defaultRows.length
    ? defaultRows
    : [{ no: "1", instruction: "", name: "", date: "" }],
});

/** Format ISO date for datetime-local input (YYYY-MM-DDThh:mm) in local time. */
function toDateTimeLocalValue(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return "";
  }
}

/** Format date for display: "Jul 28, 2025, 5:19 pm". */
export function formatMinutesDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return iso;
  }
}

/**
 * @param {Object} props
 * @param {{ rows: Array<{ no: string, instruction: string, name: string, date: string }> }} props.value
 * @param {function(*): void} props.onChange
 * @param {string} [props.error]
 * @param {string} [props.defaultName] - Default name for new rows (e.g. logged-in user)
 * @param {boolean} [props.disabled]
 */
export const MinutesTable = ({
  value,
  onChange,
  error,
  defaultName = "",
  disabled = false,
}) => {
  const data = value?.rows ? value : getDefaultMinutesTableValue();
  const { rows = [] } = data;

  const updateRow = (index, key, val) => {
    if (disabled) return;
    const next = [...rows];
    next[index] = { ...(next[index] || {}), [key]: val };
    onChange({ ...data, rows: next });
  };

  const addRow = () => {
    if (disabled) return;
    const now = new Date().toISOString();
    onChange({
      ...data,
      rows: [
        ...rows,
        {
          no: String(rows.length + 1),
          instruction: "",
          name: defaultName || "",
          date: now,
        },
      ],
    });
  };

  const removeRow = (index) => {
    if (disabled || rows.length <= 1) return;
    const next = rows
      .filter((_, i) => i !== index)
      .map((r, i) => ({ ...r, no: String(i + 1) }));
    onChange({ ...data, rows: next });
  };

  return (
    <div className="min-w-0">
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="w-full min-w-[28rem] border-collapse text-sm">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50 text-left">
              <th className="w-14 px-3 py-2 font-medium text-slate-700">No.</th>
              <th className="px-3 py-2 font-medium text-slate-700">
                Instruction
              </th>
              <th className="px-3 py-2 font-medium text-slate-700">Name</th>
              <th className="w-40 px-3 py-2 font-medium text-slate-700">
                Date
              </th>
              <th className="w-10" aria-label="Remove" />
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={index} className="border-b border-gray-100">
                <td className="px-3 py-1.5">
                  <input
                    type="text"
                    className={MINUTES_INPUT_CLASS}
                    value={row.no ?? String(index + 1)}
                    onChange={(e) => updateRow(index, "no", e.target.value)}
                    placeholder={String(index + 1)}
                    aria-label="No."
                    disabled={disabled}
                  />
                </td>
                <td className="px-3 py-1.5 align-top">
                  <textarea
                    rows={2}
                    className={`${MINUTES_INPUT_CLASS} min-h-[2.5rem] resize-y whitespace-pre-wrap break-words`}
                    value={row.instruction ?? ""}
                    onChange={(e) =>
                      updateRow(index, "instruction", e.target.value)
                    }
                    placeholder="Instruction (free entry)"
                    aria-label="Instruction"
                    disabled={disabled}
                  />
                </td>
                <td className="px-3 py-1.5">
                  <input
                    type="text"
                    className={MINUTES_INPUT_CLASS}
                    value={row.name ?? ""}
                    onChange={(e) => updateRow(index, "name", e.target.value)}
                    placeholder={defaultName || "Name"}
                    aria-label="Name"
                    disabled={disabled}
                  />
                </td>
                <td className="px-3 py-1.5">
                  <input
                    type="datetime-local"
                    className={MINUTES_INPUT_CLASS}
                    value={toDateTimeLocalValue(row.date)}
                    onChange={(e) =>
                      updateRow(
                        index,
                        "date",
                        e.target.value
                          ? new Date(e.target.value).toISOString()
                          : "",
                      )
                    }
                    title={row.date ? formatMinutesDate(row.date) : ""}
                    aria-label="Date"
                    disabled={disabled}
                  />
                </td>
                <td className="px-2 py-1.5">
                  <button
                    type="button"
                    onClick={() => removeRow(index)}
                    className="rounded p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-primary-dark/30 disabled:opacity-50 disabled:cursor-not-allowed"
                    aria-label="Remove row"
                    disabled={disabled || rows.length <= 1}
                  >
                    <Trash2 className="size-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-2">
        <button
          type="button"
          onClick={addRow}
          disabled={disabled}
          className="inline-flex items-center gap-1.5 rounded border border-dashed border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 hover:border-primary-dark hover:bg-gray-50 hover:text-primary-dark focus:outline-none focus:ring-2 focus:ring-primary-dark/30 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus className="size-3.5" />
          Add row
        </button>
      </div>
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// FeedbackTable – table for media session feedback entries
// ─────────────────────────────────────────────────────────────────────────────

/** Default structure for feedback table value */
export const getDefaultFeedbackTableValue = () => ({
  rows: [{ keyQuestions: "", keyRecommendations: "" }],
});

/**
 * @param {Object} props
 * @param {{ rows: Array<{ keyQuestions: string, keyRecommendations: string }> }} props.value
 * @param {function(*): void} props.onChange
 * @param {string} [props.error]
 * @param {boolean} [props.disabled]
 */
export const FeedbackTable = ({
  value,
  onChange,
  error,
  disabled = false,
  readOnlyView = false,
}) => {
  const firstColumnCellClass =
    readOnlyView
      ? "border border-gray-300 border-r-slate-500 px-2 py-1.5 align-top"
      : "border border-gray-300 px-2 py-1.5 align-top";
  const secondColumnCellClass =
    "border-y border-r border-l-0 border-gray-300 px-2 py-1.5 align-top";
  const firstHeaderCellClass =
    readOnlyView
      ? "border border-gray-300 border-r-slate-500 bg-gray-100 px-3 py-2 font-semibold text-slate-800"
      : "border border-gray-300 bg-gray-100 px-3 py-2 font-semibold text-slate-800";
  const secondHeaderCellClass =
    "border-y border-r border-l-0 border-gray-300 bg-gray-100 px-3 py-2 font-semibold text-slate-800";
  const actionHeaderCellClass = "w-10 border-y border-r border-l-0 border-gray-300 bg-gray-100";

  const data = value?.rows ? value : getDefaultFeedbackTableValue();
  const rows =
    Array.isArray(data.rows) && data.rows.length
      ? data.rows
      : getDefaultFeedbackTableValue().rows;

  const updateRow = (index, key, val) => {
    if (disabled) return;
    const next = [...rows];
    next[index] = { ...(next[index] || {}), [key]: val };
    onChange({ ...data, rows: next });
  };

  const addRow = () => {
    if (disabled) return;
    onChange({
      ...data,
      rows: [...rows, { keyQuestions: "", keyRecommendations: "" }],
    });
  };

  const removeRow = (index) => {
    if (disabled || rows.length <= 1) return;
    const next = rows.filter((_, i) => i !== index);
    onChange({ ...data, rows: next });
  };

  const renderCellValue = (text, placeholder) => {
    const content = String(text ?? "").trim();
    if (!content) {
      return <span className="text-slate-400">{placeholder}</span>;
    }

    return <div className="whitespace-pre-wrap break-words text-slate-700">{content}</div>;
  };

  return (
    <div className="min-w-0">
      <div className="overflow-x-auto rounded-lg border border-gray-300">
        <table
          className={
            readOnlyView
              ? "w-full table-fixed border-collapse text-sm"
              : "w-full min-w-[32rem] border-collapse text-sm"
          }
        >
          <thead>
            <tr className="text-left">
              <th className={firstHeaderCellClass}>
                Key Questions
              </th>
              <th className={secondHeaderCellClass}>
                Key Recommendations
              </th>
              {readOnlyView ? null : <th className={actionHeaderCellClass} aria-label="Remove" />}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={index}>
                <td className={firstColumnCellClass}>
                  {readOnlyView ? (
                    <div className="min-h-[2.5rem] px-1 py-1">
                      {renderCellValue(row.keyQuestions, "—")}
                    </div>
                  ) : (
                    <textarea
                      rows={3}
                      className={`${MINUTES_INPUT_CLASS} min-h-[2.5rem] resize-y whitespace-pre-wrap break-words`}
                      value={row.keyQuestions ?? ""}
                      onChange={(e) =>
                        updateRow(index, "keyQuestions", e.target.value)
                      }
                      placeholder="Enter key questions"
                      aria-label={`Key Questions row ${index + 1}`}
                      disabled={disabled}
                    />
                  )}
                </td>
                <td className={secondColumnCellClass}>
                  {readOnlyView ? (
                    <div className="min-h-[2.5rem] px-1 py-1">
                      {renderCellValue(row.keyRecommendations, "—")}
                    </div>
                  ) : (
                    <textarea
                      rows={3}
                      className={`${MINUTES_INPUT_CLASS} min-h-[2.5rem] resize-y whitespace-pre-wrap break-words`}
                      value={row.keyRecommendations ?? ""}
                      onChange={(e) =>
                        updateRow(index, "keyRecommendations", e.target.value)
                      }
                      placeholder="Enter key recommendations"
                      aria-label={`Key Recommendations row ${index + 1}`}
                      disabled={disabled}
                    />
                  )}
                </td>
                {readOnlyView ? null : (
                  <td className="px-2 py-1.5 align-top">
                    <button
                      type="button"
                      onClick={() => removeRow(index)}
                      className="rounded p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-primary-dark/30 disabled:opacity-50 disabled:cursor-not-allowed"
                      aria-label="Remove row"
                      disabled={disabled || rows.length <= 1}
                    >
                      <Trash2 className="size-4" />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {readOnlyView ? null : (
        <div className="mt-2">
          <button
            type="button"
            onClick={addRow}
            className="inline-flex items-center gap-1.5 rounded border border-dashed border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 hover:border-primary-dark hover:bg-gray-50 hover:text-primary-dark focus:outline-none focus:ring-2 focus:ring-primary-dark/30 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={disabled}
          >
            <Plus className="size-3.5" />
            Add row
          </button>
        </div>
      )}
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// SignatureBlock – displays an array of signature rows
// ─────────────────────────────────────────────────────────────────────────────

const SIGNATURE_ROW_BORDER = "border border-slate-200";
const SIGNATURE_CELL_CLASS =
  "min-h-[2.5rem] px-3 py-2 text-sm text-slate-700 bg-white flex items-center";

/**
 * Build initial value for signature-block from config defaultSignatureRows.
 * @param {Array<{ role: string, name?: string, signature?: string, timestamp?: string }>} [defaultRows]
 * @returns {Array<{ role: string, name?: string, signature?: string, timestamp?: string }>}
 */
export function getDefaultSignatureBlockValue(defaultRows = []) {
  return defaultRows.map((row) => ({
    role: row.role ?? "",
    name: row.name ?? "",
    signature: row.signature ?? "",
    timestamp: row.timestamp ?? "",
  }));
}

export function SignatureBlock({ value = [], onChange, disabled = false }) {
  const rows = Array.isArray(value) ? value : [];

  const handleRowChange = (index, key, newVal) => {
    if (!onChange || disabled) return;
    const next = rows.map((row, i) =>
      i === index ? { ...row, [key]: newVal } : row,
    );
    onChange(next);
  };

  return (
    <div className="w-full rounded-lg border border-slate-200 bg-surface-muted overflow-hidden">
      {rows.map((row, index) => (
        <div
          key={index}
          className="border-t border-slate-200 first:border-t-0 grid grid-cols-4 gap-0 items-stretch"
        >
          {/* Role label – no border */}
          <div className="min-w-[8rem] py-2 pr-3 text-sm font-medium text-primary-dark uppercase flex items-center col-span-1">
            {row.role ?? ""}
          </div>
          {/* Name */}
          <div
            className={`col-span-1 ${SIGNATURE_ROW_BORDER} border-t-0 border-r-0 border-l border-slate-200`}
          >
            {onChange && !disabled ? (
              <input
                type="text"
                value={row.name ?? ""}
                onChange={(e) => handleRowChange(index, "name", e.target.value)}
                placeholder="Name"
                className="w-full rounded-lg min-h-[2.5rem] px-3 py-2 text-sm text-slate-700 bg-white border-0 focus:outline-none focus:ring-1 focus:ring-inset focus:ring-primary-dark/30"
              />
            ) : (
              <div className={SIGNATURE_CELL_CLASS}>{row.name ?? "—"}</div>
            )}
          </div>
          {/* Signature */}
          <div
            className={`col-span-1 ${SIGNATURE_ROW_BORDER} border-t-0 border-r-0 border-l border-slate-200 flex items-center justify-center min-h-[3rem] bg-white`}
          >
            {row.signature ? (
              <img
                src={row.signature}
                alt={`Signature ${row.role ?? index}`}
                className="max-h-10 w-auto object-contain"
              />
            ) : onChange && !disabled ? (
              <input
                type="text"
                value=""
                readOnly
                placeholder="Signature"
                className="w-full rounded-lg min-h-[2.5rem] px-3 py-2 text-sm text-slate-400 bg-gray-50/50 border-0 focus:outline-none italic"
              />
            ) : (
              <span className="text-slate-400 text-sm">—</span>
            )}
          </div>
          {/* Timestamp */}
          <div
            className={`col-span-1 ${SIGNATURE_ROW_BORDER} border-t-0 border-l border-slate-200`}
          >
            {onChange && !disabled ? (
              <input
                type="text"
                value={row.timestamp ?? ""}
                onChange={(e) =>
                  handleRowChange(index, "timestamp", e.target.value)
                }
                placeholder="Date, time"
                className="w-full rounded-lg min-h-[2.5rem] px-3 py-2 text-sm text-slate-700 bg-white border-0 focus:outline-none focus:ring-1 focus:ring-inset focus:ring-primary-dark/30"
              />
            ) : (
              <div className={SIGNATURE_CELL_CLASS}>{row.timestamp ?? "—"}</div>
            )}
          </div>
        </div>
      ))}
      {rows.length === 0 && (
        <div className="border border-slate-200 rounded min-h-[3rem] flex items-center justify-center text-slate-400 text-sm">
          No signature rows
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FormSubmitActions – submit section with action buttons
// ─────────────────────────────────────────────────────────────────────────────

const SUBMIT_BTN_BASE =
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg border px-2 py-1 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60";

/**
 * @param {Object} props
 * @param {function(): void} [props.onSaveDraft] - Save to draft handler
 * @param {function(): void} [props.onPrint] - Print handler (default: shared standard-form printer)
 * @param {function(): void} [props.onCancel] - Cancel handler (e.g. reset or navigate)
 * @param {string} [props.submitLabel] - Label for submit button (default: Submit)
 * @param {boolean} [props.loadingSaveDraft] - Loading state for Save to draft
 * @param {boolean} [props.saveDraftSuccess] - Show "Saved" state after successful save (parent should clear after ~2s)
 * @param {boolean} [props.loadingPrint] - Loading state for Print
 * @param {boolean} [props.loadingCancel] - Loading state for Cancel
 * @param {function(): void} [props.onPreview] - Preview handler
 * @param {boolean} [props.loadingPreview] - Loading state for Preview
 * @param {boolean} [props.loadingSubmit] - Loading state for Send
 * @param {boolean} [props.disableSubmit] - Disable submit button (e.g. when workflow already submitted)
 * @param {boolean} [props.showPreview=false] - When true, show Preview button
 * @param {boolean} [props.showSaveDraft=true] - When false, hide Save to draft button (e.g. when form is read-only)
 * @param {boolean} [props.showSubmit=true] - When false, hide Submit button (e.g. when form is read-only)
 * @param {boolean} [props.showVerify=false] - When true, show Verify button (e.g. when workflow status is VERIFICATION)
 * @param {function(): void} [props.onVerify] - Verify button click handler
 * @param {boolean} [props.loadingVerify=false] - Loading state for Verify
 * @param {string} [props.verifyLabel='Verify'] - Label for Verify button
 * @param {boolean} [props.showReject=false] - When true, show Reject button (e.g. when status is VERIFICATION or APPROVAL)
 * @param {function(): void} [props.onReject] - Reject button click handler
 * @param {boolean} [props.loadingReject=false] - Loading state for Reject
 * @param {boolean} [props.showApprove=false] - When true, show Approve button (e.g. when workflow status is APPROVAL)
 * @param {function(): void} [props.onApprove] - Approve button click handler
 * @param {boolean} [props.loadingApprove=false] - Loading state for Approve
 * @param {boolean} [props.showDelegate=false] - When true, show Delegate button (e.g. when assignee at VERIFICATION or APPROVAL)
 * @param {function(): void} [props.onDelegate] - Delegate button click handler
 * @param {boolean} [props.loadingDelegate=false] - Loading state for Delegate
 * @param {boolean} [props.showReturn=false] - When true, show Return button (e.g. when assignee at VERIFICATION or APPROVAL)
 * @param {function(): void} [props.onReturn] - Return button click handler
 * @param {boolean} [props.loadingReturn=false] - Loading state for Return
 */
export const FormSubmitActions = ({
  onSaveDraft,
  onPrint,
  onCancel,
  onPreview,
  submitLabel = "Submit",
  loadingSaveDraft = false,
  saveDraftSuccess = false,
  loadingPrint = false,
  loadingCancel = false,
  loadingPreview = false,
  loadingSubmit = false,
  disableSubmit = false,
  showPreview = false,
  showSaveDraft = true,
  showSubmit = true,
  showVerify = false,
  onVerify,
  loadingVerify = false,
  verifyLabel = "Verify",
  showReject = false,
  onReject,
  loadingReject = false,
  showApprove = false,
  onApprove,
  loadingApprove = false,
  showDelegate = false,
  onDelegate,
  loadingDelegate = false,
  showReturn = false,
  onReturn,
  loadingReturn = false,
}) => {
  const handlePrint = useCallback(() => {
    if (typeof onPrint === "function") {
      onPrint();
      return;
    }

    printVisibleStandardForm();
  }, [onPrint]);

  const saveDraftLabel = loadingSaveDraft
    ? "Saving…"
    : saveDraftSuccess
      ? "Saved"
      : "Save to draft";
  const saveDraftIcon = loadingSaveDraft ? (
    <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden />
  ) : saveDraftSuccess ? (
    <Check className="h-4 w-4 shrink-0" aria-hidden />
  ) : (
    <Save className="h-4 w-4 shrink-0" aria-hidden />
  );

  return (
    <div className="flex w-full flex-wrap items-center justify-between gap-3 print:hidden">
      <div className="flex items-center gap-3">
        {showSaveDraft && (
          <button
            type="button"
            className={`${SUBMIT_BTN_BASE} border-gray-300 bg-white hover:bg-gray-50 transition-[color,background-color,border-color,opacity] duration-500 ease-in-out ${
              saveDraftSuccess
                ? "border-green-500 bg-green-50 text-green-700 hover:bg-green-100"
                : ""
            }`}
            onClick={onSaveDraft}
            disabled={loadingSaveDraft}
          >
            {saveDraftIcon}
            <span className="inline-block min-w-[6.5rem] text-left transition-opacity duration-500 ease-in-out">
              {saveDraftLabel}
            </span>
          </button>
        )}
        <button
          type="button"
          className={`${SUBMIT_BTN_BASE} border-gray-300 bg-white hover:bg-gray-50`}
          onClick={handlePrint}
          disabled={loadingPrint}
        >
          {loadingPrint ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          ) : (
            <Printer className="h-4 w-4" aria-hidden />
          )}
          Print
        </button>
        {showPreview && (
          <button
            type="button"
            className={`${SUBMIT_BTN_BASE} border-gray-300 bg-white hover:bg-gray-50`}
            onClick={onPreview}
            disabled={loadingPreview}
          >
            {loadingPreview ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <Eye className="h-4 w-4" aria-hidden />
            )}
            Preview
          </button>
        )}
      </div>
      <div className="flex items-center gap-3">
        <button
          type="button"
          className={`${SUBMIT_BTN_BASE} border-red-500 bg-white text-red-600 hover:bg-red-50`}
          onClick={onCancel}
          disabled={loadingCancel}
        >
          {loadingCancel ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          ) : (
            <X className="h-4 w-4" aria-hidden />
          )}
          Cancel
        </button>
        {showReject && (
          <button
            type="button"
            className={`${SUBMIT_BTN_BASE} border-red-500 bg-white text-red-600 hover:bg-red-50`}
            onClick={onReject}
            disabled={loadingReject}
          >
            {loadingReject ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <XCircle className="h-4 w-4 shrink-0" aria-hidden />
            )}
            Reject
          </button>
        )}
        {showReturn && (
          <button
            type="button"
            className={`${SUBMIT_BTN_BASE} border-amber-500 bg-white text-amber-700 hover:bg-amber-50`}
            onClick={onReturn}
            disabled={loadingReturn}
          >
            {loadingReturn ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <ArrowLeft className="h-4 w-4 shrink-0" aria-hidden />
            )}
            Return
          </button>
        )}
        {showDelegate && (
          <button
            type="button"
            className={`${SUBMIT_BTN_BASE} border-slate-400 bg-white text-slate-700 hover:bg-slate-50`}
            onClick={onDelegate}
            disabled={loadingDelegate}
          >
            {loadingDelegate ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <UserPlus className="h-4 w-4 shrink-0" aria-hidden />
            )}
            Delegate
          </button>
        )}
        {showVerify && (
          <button
            type="button"
            className={`${SUBMIT_BTN_BASE} border-primary-dark bg-primary text-white hover:bg-secondary`}
            onClick={onVerify}
            disabled={loadingVerify}
          >
            {loadingVerify ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden />
            )}
            {verifyLabel}
          </button>
        )}
        {showApprove && (
          <button
            type="button"
            className={`${SUBMIT_BTN_BASE} border-primary-dark bg-primary text-white hover:bg-secondary`}
            onClick={onApprove}
            disabled={loadingApprove}
          >
            {loadingApprove ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden />
            )}
            Approve
          </button>
        )}
        {showSubmit && (
          <button
            type="submit"
            className={`${SUBMIT_BTN_BASE} border-primary-dark bg-primary text-white hover:bg-secondary`}
            disabled={loadingSubmit || disableSubmit}
          >
            {loadingSubmit ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <Send className="h-4 w-4" aria-hidden />
            )}
            {submitLabel}
          </button>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// BoardFooter – footer section displaying board members
// ─────────────────────────────────────────────────────────────────────────────

/**
 * @param {Object} props
 * @param {string} [props.heading] - Heading text (default: 'Board of Directors')
 * @param {Array<{ name?: string, role?: string }>} [props.boardMembers] - List of board members
 * @param {string} [props.className] - Additional class names
 */
export const BoardFooter = ({
  heading = "Board of Directors",
  boardMembers = [],
  className = "",
}) => {
  const membersText = boardMembers
    .map((m) => `${m.name ?? ""}- ${m.role ?? ""}`.replace(/- $/, ""))
    .filter(Boolean)
    .join(", ");

  return (
    <div
      className={`w-full border-t border-primary pt-3 mt-1 ${className}`.trim()}
    >
      <p className="text-xxs text-primary text-center w-full font-bold uppercase">
        {heading}
      </p>
      {membersText && (
        <p className="text-xxs text-slate-600 text-center w-full mt-1">
          {membersText}
        </p>
      )}
    </div>
  );
};

export { ToAndUfs } from "./ToAndUfs";
export {
  FileTypeFileNameSection,
  canShowFileTypeFileNameSection,
} from "./FileTypeFileNameSection";
export { UFS } from "./UFS";
export { MultipleLinesInput } from "./MultipleLinesInput";
export { LocationSelector } from "./LocationSelector";
export { UserSelector } from "./UserSelector";
export { FileUpload } from "./FileUpload";
export { AgeGroupSelector } from "./AgeGroupSelector";
export { default as IecMaterialsTypeFormatQuantity } from "./IecMaterialsTypeFormatQuantity";
